// Conversation-pane file drop.
//
// A composer used to be the ONLY thing that accepted a dragged file, so the
// drop had to land on the input box itself. People expect the whole
// conversation to accept it — the message list included. This module lifts the
// drop target from the composer card to its conversation pane (the element
// marked `data-drop-pane`, normally the header + message list + composer
// column) and paints one overlay over that pane while a file is in flight.
//
// It is deliberately shared: the three composers (RichMessageInput, the classic
// MessageInput, AgentComposer) had three copies of the same enter/over/leave/
// drop logic, so a fix had to be made three times. Dropped files still flow
// into each composer's OWN addFiles → size-check → upload pipeline; this only
// changes where the drop is accepted, never what happens to the files.

/** True only for a drag carrying files — never a text, link or element drag. */
export function isFileDrag(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}

export interface PaneDropOptions {
  /** Read at event time, not attach time, so a composer can gate live. */
  disabled?: () => boolean;
  onFiles: (files: File[]) => void;
}

export class PaneDrop {
  /** Drives the overlay. */
  active = $state(false);
  /** The pane the overlay renders into. Null until attached. */
  pane = $state<HTMLElement | null>(null);

  // dragenter/dragleave BUBBLE, so crossing from a parent onto one of its
  // children fires leave-then-enter on the pane. Counting them keeps the
  // overlay steady instead of flickering once per element the cursor crosses —
  // the classic naive-implementation bug. Balanced by construction: every
  // enter inside the pane has a matching leave.
  #depth = 0;

  #reset = (): void => {
    this.#depth = 0;
    this.active = false;
  };

  /**
   * Attach to `node`'s conversation pane. Returns a teardown for `$effect`.
   * Falls back to `node` itself when no ancestor is marked, so an
   * un-annotated surface keeps exactly its previous composer-only behaviour.
   *
   * Panes must not nest. No two marked panes contain one another today — every
   * conversation surface is a flex sibling or its own route — and nothing here
   * calls stopPropagation, so nesting one inside another would let both take
   * the same dropped file. Mark the inner surface only, or teach this to skip
   * an event a nested pane already claimed.
   */
  attach(node: HTMLElement, opts: PaneDropOptions): () => void {
    const pane = node.closest<HTMLElement>("[data-drop-pane]") ?? node;
    const blocked = (): boolean => opts.disabled?.() ?? false;

    // The overlay is an `absolute inset-0` child of the pane, so the pane has to
    // be a positioned ancestor. Most already are; give the rest a `relative` and
    // put it back on teardown.
    const wasStatic = getComputedStyle(pane).position === "static";
    if (wasStatic) pane.style.position = "relative";
    this.pane = pane;

    const onEnter = (e: DragEvent): void => {
      if (blocked() || !isFileDrag(e)) return;
      e.preventDefault();
      this.#depth += 1;
      if (this.#depth === 1) this.active = true;
    };

    const onOver = (e: DragEvent): void => {
      if (blocked() || !isFileDrag(e)) return;
      // Required: without preventDefault here the browser never dispatches the
      // drop, and navigates to the file instead.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };

    const onLeave = (e: DragEvent): void => {
      if (blocked() || !isFileDrag(e)) return;
      this.#depth = Math.max(0, this.#depth - 1);
      if (this.#depth === 0) this.active = false;
    };

    const onDrop = (e: DragEvent): void => {
      this.#reset();
      if (blocked() || !isFileDrag(e)) return;
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) opts.onFiles(files);
    };

    pane.addEventListener("dragenter", onEnter);
    pane.addEventListener("dragover", onOver);
    pane.addEventListener("dragleave", onLeave);
    pane.addEventListener("drop", onDrop);
    // A drag cancelled with Escape, or dropped somewhere else entirely, fires
    // no dragleave on the pane — without these the overlay latches on forever.
    window.addEventListener("dragend", this.#reset);
    window.addEventListener("drop", this.#reset);

    return () => {
      pane.removeEventListener("dragenter", onEnter);
      pane.removeEventListener("dragover", onOver);
      pane.removeEventListener("dragleave", onLeave);
      pane.removeEventListener("drop", onDrop);
      window.removeEventListener("dragend", this.#reset);
      window.removeEventListener("drop", this.#reset);
      if (wasStatic) pane.style.position = "";
      this.pane = null;
      this.#reset();
    };
  }
}

let guardInstalled = false;

/**
 * Make a file dropped ANYWHERE else inert.
 *
 * Without this, dropping a file on the sidebar, a header or a tasks route
 * navigates the tab to `file:///…` and throws away unsent composer text. (The
 * desktop shell only escaped that because its will-navigate allowlist happens
 * to reject `file://`.)
 *
 * Both listeners are bubble-phase and never call stopPropagation, so every real
 * drop target — the panes here, the tasks board, calendar, attachments widget,
 * pages list, channel sidebar — has already run and already prevented the
 * default by the time this sees the event. When nothing did, the cursor is set
 * to "no-drop" so the page stops advertising a target it will not honour.
 */
export function installInertFileDropGuard(): void {
  if (guardInstalled || typeof window === "undefined") return;
  guardInstalled = true;

  window.addEventListener("dragover", (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    if (!e.defaultPrevented && e.dataTransfer) e.dataTransfer.dropEffect = "none";
    e.preventDefault();
  });
  window.addEventListener("drop", (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
  });
}
